import { FfsRegionClassification } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsRegionClassificationSchema'
import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'

import {
  GoverningSelection,
  selectGoverningReport,
  UnassessedReason,
} from './ffsGoverningReport'
import { DeratePressure } from './ffsPressure'
import { FfsReportRecord } from './ffsReportRecord'
import { ScenarioKey } from './ffsScenarioKey'
import {
  aggregateVerdicts,
  isVerdict,
  Verdict,
  VerdictChild,
} from './ffsSeverity'

/**
 * The conservative rollup: LTA -> component -> unit.
 *
 * Two axes are kept deliberately separate. SEVERITY (`ffsSeverity`) only means
 * anything once a scope is COMPLETE, which is what this module decides. An
 * incomplete scope reports neither a verdict nor a failure — it makes no claim
 * at all — so `pending` is a state here, never a rank.
 */

export type LtaDisposition =
  | {
      kind: 'verdict'
      result: Verdict
      /**
       * `not-assessable` marks a Type C region: a Fail derived from its
       * CLASSIFICATION, with no report behind it. The UI must say "not
       * demonstrable at Level 1/2" rather than "failed criteria".
       */
      reason?: 'not-assessable'
      report: FfsReportRecord | null
      disagreesWithDefault: boolean
      timestampUnusable: boolean
    }
  | { kind: 'skipped'; report: FfsReportRecord }
  | { kind: 'unassessed'; reason: LtaUnassessedReason }

/**
 * Selector outcomes, plus the two an LTA can fail at before any report is even
 * considered: it was never classified, or its own indication entry would not
 * parse (`entryToIndication` throws on pre-migration `data.stats`).
 */
export type LtaUnassessedReason =
  | UnassessedReason
  | 'no-classification'
  | 'unparseable-indication'

export interface ScopeCounts {
  /** EVERY active LTA in the set, including ones with no classification. */
  total: number
  /** Verdict-bearing, including Type C. */
  assessed: number
  skipped: number
  /** Type C; already folded into `assessed` and into the severity result. */
  notAssessable: number
  unassessed: number
}

export const EMPTY_COUNTS: ScopeCounts = {
  total: 0,
  assessed: 0,
  skipped: 0,
  notAssessable: 0,
  unassessed: 0,
}

export type ScopeRollup =
  | {
      kind: 'verdict'
      result: Verdict
      /** Every child at the winning severity -- ties are all recorded. */
      governingIds: string[]
      /** Meaningful only when `result` is `derate`. */
      derate: DeratePressure | null
      counts: ScopeCounts
    }
  | { kind: 'incomplete'; counts: ScopeCounts }
  /** No LTA set entry exists -- binning was never run. Blocking. */
  | { kind: 'not-started' }
  /** The set exists and is empty -- binning ran and found nothing. Not blocking. */
  | { kind: 'no-ltas-found' }
  /** Everything in scope was skipped. Not blocking, but no verdict. */
  | { kind: 'no-verdict'; counts: ScopeCounts }

/** A scope that cannot be signed off until the engineer acts. */
export const isBlocking = (rollup: ScopeRollup): boolean =>
  rollup.kind === 'incomplete' || rollup.kind === 'not-started'

/** A scope's counts, or zeros for the states that carry none. */
export const countsOf = (rollup: ScopeRollup): ScopeCounts =>
  'counts' in rollup ? rollup.counts : EMPTY_COUNTS

export interface DisposeLtaArgs {
  /** `null` when the LTA was never classified, or its record was malformed. */
  classification: FfsRegionClassification | null
  /** The indication entry itself would not parse — see `ffsUnitAssembly`. */
  indicationParseFailed?: boolean
  reports: ReadonlyArray<FfsReportRecord>
  scenarioKey: ScenarioKey | null
  parseFailures: number
}

/**
 * Resolve ONE LTA.
 *
 * Takes the classification rather than a whole `ClassifiedLta` because that is
 * all it reads — which also lets an LTA whose indication entry failed to parse
 * be disposed without fabricating a `RolodexIndication` for it.
 *
 * Type C is branched FIRST, before scenario selection and before reports are
 * considered at all. It routes to `NEITHER_APPLICABLE`, so it can never receive
 * a Part 4/5 report; requiring one would deadlock close-out forever. Its Fail
 * comes from the classification itself, and malformed reports tagged to it are
 * irrelevant rather than blocking.
 */
export function disposeLta({
  classification,
  indicationParseFailed = false,
  reports,
  scenarioKey,
  parseFailures,
}: DisposeLtaArgs): LtaDisposition {
  // An unreadable indication means its classification cannot be trusted either,
  // so this is checked before Type C rather than after.
  if (indicationParseFailed) {
    return { kind: 'unassessed', reason: 'unparseable-indication' }
  }
  // Fail closed: an unclassified LTA cannot be routed, so it cannot be assessed.
  if (!classification) {
    return { kind: 'unassessed', reason: 'no-classification' }
  }

  if (classification.componentType === 'C') {
    return {
      kind: 'verdict',
      result: FfsResultType.Fail,
      reason: 'not-assessable',
      report: null,
      disagreesWithDefault: false,
      timestampUnusable: false,
    }
  }

  const selection: GoverningSelection = selectGoverningReport({
    classification,
    reports,
    scenarioKey,
    parseFailures,
  })

  if (selection.kind === 'unassessed') {
    return { kind: 'unassessed', reason: selection.reason }
  }

  const { report, disagreesWithDefault, timestampUnusable } = selection
  if (report.result === FfsResultType.Skipped) {
    return { kind: 'skipped', report }
  }
  if (!isVerdict(report.result)) {
    // `pending` is filtered out upstream; this is belt-and-braces.
    return { kind: 'unassessed', reason: 'pending' }
  }

  return {
    kind: 'verdict',
    result: report.result,
    report,
    disagreesWithDefault,
    timestampUnusable,
  }
}

function countDispositions(
  dispositions: ReadonlyArray<{ disposition: LtaDisposition }>
): ScopeCounts {
  let assessed = 0
  let skipped = 0
  let notAssessable = 0
  for (const { disposition } of dispositions) {
    if (disposition.kind === 'verdict') {
      assessed += 1
      if (disposition.reason === 'not-assessable') notAssessable += 1
    } else if (disposition.kind === 'skipped') {
      skipped += 1
    }
  }
  const total = dispositions.length
  return {
    total,
    assessed,
    skipped,
    notAssessable,
    unassessed: total - assessed - skipped,
  }
}

/** `assessed / (total - skipped)`, or `null` when everything was skipped. */
export function progressOf(
  counts: ScopeCounts
): { assessed: number; expected: number } | null {
  const expected = counts.total - counts.skipped
  return expected === 0 ? null : { assessed: counts.assessed, expected }
}

/**
 * Finish a scope once its blocking states have been ruled out: either it has
 * verdict-bearing children or it has none, and the latter is `no-verdict` rather
 * than a Pass.
 */
const verdictOrNone = (
  children: ReadonlyArray<VerdictChild>,
  counts: ScopeCounts
): ScopeRollup => {
  const aggregate = aggregateVerdicts(children)
  return aggregate
    ? { kind: 'verdict', ...aggregate, counts }
    : { kind: 'no-verdict', counts }
}

export interface ComponentLtaEntry {
  ltaId: string
  disposition: LtaDisposition
}

export interface RollupComponentArgs {
  /** Whether an LTA indication-set entry was found for this component. */
  setExists: boolean
  ltas: ReadonlyArray<ComponentLtaEntry>
}

export function rollupComponent({
  setExists,
  ltas,
}: RollupComponentArgs): ScopeRollup {
  if (!setExists) return { kind: 'not-started' }
  if (ltas.length === 0) return { kind: 'no-ltas-found' }

  const counts = countDispositions(ltas)
  if (counts.unassessed > 0) return { kind: 'incomplete', counts }

  return verdictOrNone(
    ltas.flatMap(({ ltaId, disposition }) =>
      disposition.kind === 'verdict'
        ? [
            {
              id: ltaId,
              result: disposition.result,
              // A Type C verdict has no report, and so no pressure -- but it is
              // a Fail, so it can never be the derating child anyway.
              pressure: disposition.report?.mawpr ?? null,
            },
          ]
        : []
    ),
    counts
  )
}

export interface ComponentRollupEntry {
  componentId: string
  rollup: ScopeRollup
}

export function rollupUnit(
  components: ReadonlyArray<ComponentRollupEntry>
): ScopeRollup {
  const counts = components.reduce<ScopeCounts>((acc, { rollup }) => {
    const c = countsOf(rollup)
    return {
      total: acc.total + c.total,
      assessed: acc.assessed + c.assessed,
      skipped: acc.skipped + c.skipped,
      notAssessable: acc.notAssessable + c.notAssessable,
      unassessed: acc.unassessed + c.unassessed,
    }
  }, EMPTY_COUNTS)

  // One blocked component blocks the unit: a conservative rollup cannot claim a
  // verdict while any part of the scope is unresolved.
  if (components.some(({ rollup }) => isBlocking(rollup))) {
    return { kind: 'incomplete', counts }
  }

  return verdictOrNone(
    components.flatMap(({ componentId, rollup }) =>
      rollup.kind === 'verdict'
        ? [
            {
              id: componentId,
              result: rollup.result,
              // `unavailable` propagates as a null, making the unit aggregate
              // unavailable too -- the conservative reading.
              pressure:
                rollup.derate?.kind === 'known' ? rollup.derate.mawp : null,
            },
          ]
        : []
    ),
    counts
  )
}
